import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as EmptyAttachment } from '../../../../../../assets/billpay/bill-no-attachment.svg';
import { ReactComponent as CloseIcon } from '../../../../../../assets/svg/invoices-icons/icon-close-icon.svg';
import { ReactComponent as ScannerIcon } from '../../../../../../assets/billpay/bill-scan.svg';
import { UserMessage } from '../../../../../../DesignSystem/LiliUserMessage/UserMessage';
import Heading from '../../../../../../generic-components/Heading';
import { DropArea } from '../../../../../../components/form/DropArea/DropArea';
import { ACCEPTED_BILL_TYPES, BILLS_STATUSES } from '../../../../consts';
import { MAX_FILE_SIZE_MB } from '../../../AddBill/consts';
import { ReactComponent as UploadBillIcon } from '../../../../../../assets/billpay/upload-bill.svg';
import { useUploadAttachment } from '../../../../hooks/useUploadAttachment';
import { useStore } from '../../../../../../store';
import { featureAccess, LimitedFeatureType } from '../../../../../../services/appAccess.service';
import { FEATURE_FLAG } from '../../../../../../constants/general';
import { FeatureAccess } from '../../../../../AccountSettings/components/rolesAndPermissions/PermissionsList';

interface IProps {
  billId: string;
  billStatus: string;
}

export const NoAttachment = ({ billId, billStatus }: IProps) => {
  const [isUserMessage, setIsUserMessage] = useState<boolean>(false);
  const shouldShowUploadFlow = useMemo(() => isUserMessage, [isUserMessage]);
  const setUpdatedUnpaidBillById = useStore((state) => state.setUpdatedUnpaidBillById);
  const setUpdatedPaidBillById = useStore((state) => state.setUpdatedPaidBillById);
  const billPayPermission = featureAccess(FEATURE_FLAG.BILL_PAY, LimitedFeatureType.BillPay);

  const { t } = useTranslation();

  const {
    cancelReq,
    mutation: { mutateAsync, isLoading: uploadingLoader },
  } = useUploadAttachment();

  const handleDropAreaFiles = (filesList: FileList) => {
    const list = Array.from(filesList);
    const fileToUpload = list[0];
    mutateAsync({ file: fileToUpload, billId }).then((res) => {
      const docId = res.documentId;
      if (billStatus === BILLS_STATUSES.PENDING) {
        setUpdatedUnpaidBillById(billId, docId);
      } else {
        setUpdatedPaidBillById(billId, docId);
      }
    });
    setIsUserMessage(false);
  };

  const handleCloseUserMessage = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    if (billPayPermission !== FeatureAccess.READ_WRITE) return;
    setIsUserMessage((prevState) => !prevState);
  };

  return (
    <div>
      <EmptyAttachment
        className={`${billPayPermission !== FeatureAccess.READ_WRITE ? 'cursor-not-allowed' : ''}`}
        onClick={handleCloseUserMessage}
      />
      {shouldShowUploadFlow && (
        <UserMessage
          id='uploadAttachmentFromHub'
          onClick={() => {
            setIsUserMessage((prevState) => !prevState);
          }}
          mainWrapperOverrideClassName='bg-[#dfe2eb] flex justify-center fixed inset-0 h-screen w-screen z-50 items-center outline-none'
        >
          <CloseIcon
            className='absolute top-5 right-3 rounded-full fill-bw07 hover:bg-bw02'
            onClick={handleCloseUserMessage}
          />
          {uploadingLoader ? (
            <div className='flex flex-col items-center'>
              <ScannerIcon className='mb-[24px]' />
              <Heading title={t('UPLOAD_BILL.LOADER_TITLE')} className='font-metricR text-[22px] text-bw08' />
              <Heading
                title={t('UPLOAD_BILL.LOADER_DISCLAIMER')}
                className='font-metricR text-[16px] text-bw07 mb-[24px]'
              />
              <button onClick={cancelReq} type='button' className='font-metricR text-[18px] text-bw08'>
                {t('UPLOAD_BILL.CANCEL_UPLOAD')}
              </button>
            </div>
          ) : (
            <div className='rounded-[12px] bg-bw01 flex flex-col'>
              <Heading
                title={t('UPLOAD_BILL.DROP_AREA_TITLE')}
                className='font-metricR text-[16px] text-bw08 py-[20px] px-[24px] border-b border-bw03'
              />
              <div className='w-[500px] h-[500px] py-[24px] px-[24px]'>
                <DropArea
                  acceptedTypes={ACCEPTED_BILL_TYPES}
                  maxFileSizeInMb={MAX_FILE_SIZE_MB}
                  IconAsProp={UploadBillIcon}
                  onFilesChange={handleDropAreaFiles}
                />
              </div>
            </div>
          )}
        </UserMessage>
      )}
    </div>
  );
};
